import type { Tile } from './grid';

/**
 * A handheld tool as the sim sees it (content maps tool definitions onto this). Levels never
 * require tools: they're conveniences, so the generator validates lawns without them.
 */
export interface ToolSpec {
  id: string;
  charges: number;
  cooldownTicks: number;
  /** How far it reaches, in tiles. */
  range: number;
  /** Shoos every critter within `range` of the mower. */
  shoos: boolean;
  /** Tile kinds it clears (turns into ground) along the lane ahead, up to `range` tiles. */
  clears: readonly Tile[];
  /**
   * When set, only these tiles (index y * width + x) may be cleared: for a kind shared by other
   * tiles (a rock is an obstacle like a tree or a house, but only the rock digs out).
   */
  only?: readonly number[];
  /** Fills (flattens) molehills in the lane ahead, as if mowed over. */
  flattens?: boolean;
  /** Trims the grass in the lane ahead right down (the string trimmer), as if mowed. */
  trims?: boolean;
  /**
   * Works on every tile within `range` all round the mower (a square), instead of the lane ahead:
   * the string trimmer, swung round.
   */
  around?: boolean;
  /** Turns off every sprinkler within `range` tiles of the mower (the sprinkler key). */
  shutsOff?: boolean;
  /** Sends every critter but the sheep home, off the lawn for this many ticks (the bike horn). */
  sendsHome?: number;
}

export interface ToolState {
  charges: number;
  /** Ticks until it can be used again. */
  cooldown: number;
}

export function initialToolStates(tools: readonly ToolSpec[]): ToolState[] {
  return tools.map((t) => ({ charges: t.charges, cooldown: 0 }));
}

export function validateToolSpec(t: ToolSpec): void {
  if (!Number.isInteger(t.charges) || t.charges < 1)
    throw new RangeError(`${t.id}: charges must be >= 1`);
  if (!Number.isInteger(t.range) || t.range < 1)
    throw new RangeError(`${t.id}: range must be >= 1`);
  if (!t.shoos && t.clears.length === 0 && !t.flattens && !t.trims && !t.shutsOff && !t.sendsHome)
    throw new RangeError(`${t.id}: a tool must do something`);
}
